import { z } from "zod";

export const quoteTextPositionSchema = z.object({
  id: z.string().min(1).max(200),
  kurztext: z.string().min(1).max(5000),
  menge: z.number().finite(),
  einheit: z.string().max(80),
  teil: z.enum(["A", "B", "C"]),
  variant: z.boolean().optional(),
});
export const quoteTextPlausibilityInputSchema = z.object({
  text: z.string().trim().min(1, "Bitte eine Leistungsbeschreibung eingeben.").max(30000),
  positions: z.array(quoteTextPositionSchema).max(1000),
}).superRefine((input, ctx) => {
  if (input.text.length + input.positions.reduce((sum, p) => sum + p.kurztext.length, 0) > 160000) {
    ctx.addIssue({ code: "custom", message: "Die Beschreibung und Positionstexte sind für eine einzelne Prüfung zu lang." });
  }
  if (new Set(input.positions.map((p) => p.id)).size !== input.positions.length) {
    ctx.addIssue({ code: "custom", message: "Positionskennungen müssen eindeutig sein." });
  }
});
const text = z.string().trim().min(1).max(5000);
const referenced = { positionId: z.string().min(1).max(200), begruendung: text };
const findingSchemas = {
  fehlt: z.object({ leistung: text, zitat: text }).strict(),
  unklar: z.object({ ...referenced, zitat: text }).strict(),
  nicht_beschrieben: z.object(referenced).strict(),
  teil_hinweis: z.object({ ...referenced, zitat: text }).strict(),
};
export const quoteTextPlausibilityResultSchema = z.object({
  fehlt: z.array(findingSchemas.fehlt).max(200),
  unklar: z.array(findingSchemas.unklar).max(200),
  nicht_beschrieben: z.array(findingSchemas.nicht_beschrieben).max(1000),
  teil_hinweis: z.array(findingSchemas.teil_hinweis).max(200),
}).strict();
const reasons = z.array(z.enum(["zitat_nicht_belegt", "position_unbekannt"])).min(1).max(2);
const rejectedFindingSchema = z.discriminatedUnion("kategorie", [
  z.object({ kategorie: z.literal("fehlt"), hinweis: findingSchemas.fehlt, gruende: reasons }).strict(),
  z.object({ kategorie: z.literal("unklar"), hinweis: findingSchemas.unklar, gruende: reasons }).strict(),
  z.object({ kategorie: z.literal("nicht_beschrieben"), hinweis: findingSchemas.nicht_beschrieben, gruende: reasons }).strict(),
  z.object({ kategorie: z.literal("teil_hinweis"), hinweis: findingSchemas.teil_hinweis, gruende: reasons }).strict(),
]);
// Provider output stays unchanged and strict. Only our API adds evidence warnings.
const originalQuote = z.string().trim().min(1).max(30000);
export const quoteTextPlausibilityResponseSchema = quoteTextPlausibilityResultSchema.extend({
  // Restoring original whitespace can make a verified quote longer than
  // the provider's 5000-character quote, up to the input description limit.
  fehlt: z.array(findingSchemas.fehlt.extend({ zitat: originalQuote })).max(200),
  unklar: z.array(findingSchemas.unklar.extend({ zitat: originalQuote })).max(200),
  teil_hinweis: z.array(findingSchemas.teil_hinweis.extend({ zitat: originalQuote })).max(200),
  nicht_belegbar: z.array(rejectedFindingSchema).max(1600).optional(),
}).strict();
export type QuoteTextPosition = z.infer<typeof quoteTextPositionSchema>;
export type QuoteTextPlausibilityInput = z.infer<typeof quoteTextPlausibilityInputSchema>;
export type QuoteTextPlausibilityResult = z.infer<typeof quoteTextPlausibilityResultSchema>;
export type QuoteTextPlausibilityResponse = z.infer<typeof quoteTextPlausibilityResponseSchema>;
